import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { UserRound, Plus, Pencil, Trash2, IdCard, Weight } from "lucide-react";

export default function PilotList() {
  const [items, setItems] = useState(null);
  const navigate = useNavigate();

  const load = async () => setItems((await api.get("/pilots")).data);
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    await api.delete(`/pilots/${id}`);
    toast.success("Pilote supprimé");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-cyan-500/80">
            <UserRound className="h-3.5 w-3.5" /> Équipage
          </div>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-slate-50">Pilotes</h1>
          <p className="mt-1 text-sm text-slate-400">Fiches réutilisables : infos, poids (centrage) et documents avec validité.</p>
        </div>
        <Button data-testid="add-pilot-button" onClick={() => navigate("/pilots/new")}
          className="bg-cyan-500 font-semibold text-[#070C18] hover:bg-cyan-400">
          <Plus className="mr-1.5 h-4 w-4" /> Nouveau pilote
        </Button>
      </div>

      {items === null ? (
        <div className="py-16 text-center text-slate-500">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="glass rounded-2xl py-16 text-center">
          <UserRound className="mx-auto mb-3 h-10 w-10 text-slate-600" />
          <p className="text-slate-400">Aucun pilote enregistré.</p>
          <Button onClick={() => navigate("/pilots/new")} variant="link" className="mt-1 text-cyan-400">Créer votre première fiche pilote</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((p) => (
            <div key={p.id} data-testid={`pilot-card-${p.id}`}
              className="glass group rounded-2xl p-5 transition-all hover:border-cyan-500/50 hover:shadow-[0_0_20px_rgba(0,229,255,0.15)]">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-cyan-500/25 bg-cyan-500/10">
                    <UserRound className="h-5 w-5 text-cyan-400" />
                  </div>
                  <div>
                    <div className="font-display text-base font-semibold text-slate-100">
                      {p.firstName} {p.lastName}
                    </div>
                    <div className="text-xs text-slate-400">{p.licenseType || "—"}</div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => navigate(`/pilots/${p.id}`)} data-testid={`edit-pilot-${p.id}`}
                    className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-cyan-300"><Pencil className="h-4 w-4" /></button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button data-testid={`delete-pilot-${p.id}`}
                        className="rounded-lg p-2 text-slate-400 hover:bg-rose-500/10 hover:text-rose-300"><Trash2 className="h-4 w-4" /></button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="border-slate-700 bg-[#0D1527] text-slate-100">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer ce pilote ?</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">{p.firstName} {p.lastName} sera supprimé.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="border-slate-700 bg-transparent text-slate-300">Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(p.id)} className="bg-rose-600 hover:bg-rose-500">Supprimer</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 border-t border-slate-800 pt-4 text-center">
                <div>
                  <IdCard className="mx-auto h-4 w-4 text-cyan-500/70" />
                  <div className="mt-1 font-mono-data text-xs text-slate-200">{p.licenseNumber || "—"}</div>
                  <div className="text-[10px] uppercase text-slate-500">N° licence</div>
                </div>
                <div>
                  <Weight className="mx-auto h-4 w-4 text-cyan-500/70" />
                  <div className="mt-1 font-mono-data text-xs text-slate-200">{p.weight || 0} kg</div>
                  <div className="text-[10px] uppercase text-slate-500">Poids</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
